"use client";

import { useBreadcrumb } from "@/app/_utils/context/BreadcrumbContext";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React from "react";

/**
 * Routes where breadcrumbs should not be displayed.
 */
const ROUTES_WITHOUT_BREADCRUMBS = [
  "/log-in",
  "/sign-up", 
];

/**
 * Adds breadcrumb trail to pages.
 *
 * @returns Formatted Breadcrumb.
 */
export default function BreadcrumbComponent(): React.ReactElement {
  const { breadcrumbs } = useBreadcrumb();
  const pathname = usePathname();

  // Hide breadcrumbs for auth routes
  if (ROUTES_WITHOUT_BREADCRUMBS.some(route => pathname === route || pathname.startsWith(route + "/"))) {
    return <div />;
  }

  // For public directory pages, filter out "Home" breadcrumb
  const isPublicPage = pathname.startsWith('/directory');
  const filteredBreadcrumbs = isPublicPage 
    ? breadcrumbs.filter(b => b.title !== 'Home')
    : breadcrumbs;

  if (filteredBreadcrumbs.length <= 1) {
    // Empty div when no breadcrumbs
    return <div />;
  }

  return (
    <nav className="flex items-center space-x-1 text-sm text-muted-foreground overflow-hidden" aria-label="breadcrumb">
      <Breadcrumb>
        <BreadcrumbList className="mb-0 flex items-center">
          {filteredBreadcrumbs.map((breadcrumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && (
                <BreadcrumbSeparator className="text-muted-foreground/60 shrink-0" />
              )}
              <BreadcrumbItem className="shrink-0">
                {i !== breadcrumbs.length - 1 ? (
                  <BreadcrumbLink asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-1 text-sm! font-medium text-muted-foreground hover:text-foreground truncate max-w-32 md:max-w-none"
                      asChild
                    >
                      <Link href={breadcrumb.href} title={breadcrumb.title} className="no-underline">
                        {breadcrumb.title}
                      </Link>
                    </Button>
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage className="h-auto p-1 text-sm font-medium text-primary truncate max-w-32 md:max-w-none" title={breadcrumb.title}>
                    {breadcrumb.title}
                  </BreadcrumbPage>
                )}
              </BreadcrumbItem>
            </React.Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    </nav>
  );
}
